import { AxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useResultsStore, useStiffeningStore, useElementLoadStore } from '@editorStores'
import {
  useElementCrossSectionAssignment,
  useNotification,
  useResultsInvalidation,
} from '@editorHooks'
import {
  getLoadsPerElement,
  getDesignPositionBundle,
  getElementChecksBundle,
  getHorizontalForcesPerSegment,
  getMemberCheckSettings,
  getAnchorInterventionTableData,
  getAnchorChecks,
  getDesignForces,
  getAllCharacteristicMemberSupportReactions,
  getResultsValid,
  getStiffeningIntervals,
  getModel,
  getProjectConnectionPrioritization,
  getElementLoads,
} from '@queries'
import { computeAnchorCheckResults, computeResults } from '@mutations'
import { buildErrorMessage } from 'src/constants'
import useCalculateSnackbar from './useCalculateSnackbar'

const useResultsQueries = (projectId: string) => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { triggerNotification } = useNotification()

  const invalidateResults = useResultsInvalidation()

  // STORE

  const clearResults = useResultsStore(state => state.clear)

  const setStiffeningSegmentChecks = useResultsStore(state => state.setStiffeningSegmentChecks)
  const setStiffeningIntervals = useStiffeningStore(state => state.setStiffeningIntervals)

  const setElementLoads = useResultsStore(state => state.setElementLoads)
  const setPositionLoads = useResultsStore(state => state.setPositionLoads)
  const setManualLoadsPerElements = useElementLoadStore(state => state.setLoadsPerElements)

  const setMemberCheckSettings = useResultsStore(state => state.setMemberCheckSettings)
  const setDesignForces = useResultsStore(state => state.setDesignForces)
  const setStructuralChecks = useResultsStore(state => state.setStructuralChecks)
  const setStandardRipChecks = useResultsStore(state => state.setStandardRipChecks)
  const setAnchorChecks = useResultsStore(state => state.setAnchorChecks)
  const setCharacteristicMemberSupportReactions = useResultsStore(
    state => state.setCharacteristicMemberSupportReactions,
  )
  const setAnchorInterventionTableData = useResultsStore(
    state => state.setAnchorInterventionTableData,
  )
  const setWallRipsPositionGrouping = useResultsStore(state => state.setWallRipsPositionGrouping)
  const setWallLintelsPositionGrouping = useResultsStore(
    state => state.setWallLintelsPositionGrouping,
  )
  const setSlabBeamsPositionGrouping = useResultsStore(state => state.setSlabBeamsPositionGrouping)
  const setRoofSlabBeamsPositionGrouping = useResultsStore(
    state => state.setRoofSlabBeamsPositionGrouping,
  )
  const setBeamsPositionGrouping = useResultsStore(state => state.setBeamsPositionGrouping)
  const setColumnsPositionGrouping = useResultsStore(state => state.setColumnsPositionGrouping)
  const setPurlinsPositionGrouping = useResultsStore(state => state.setPurlinsPositionGrouping)
  const setMemberCheckSettingsFromLastComputation = useResultsStore(
    state => state.setMemberCheckSettingsFromLastComputation,
  )
  const setCrossSectionsFromLastComputation = useResultsStore(
    state => state.setCrossSectionsFromLastComputation,
  )
  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const elementCrossSectionAssignment = useElementCrossSectionAssignment()

  const abortLoadingResults = () => {
    queryClient.cancelQueries(getDesignPositionBundle.getKey(projectId))
    queryClient.cancelQueries(getMemberCheckSettings.getKey(projectId))
    queryClient.cancelQueries(getDesignForces.getKey(projectId))
    queryClient.cancelQueries(getElementChecksBundle.getKey(projectId))
    queryClient.cancelQueries(getLoadsPerElement.getKey(projectId))
    queryClient.cancelQueries(getAnchorInterventionTableData.getKey(projectId))
    queryClient.cancelQueries(getAnchorChecks.getKey(projectId))
    queryClient.cancelQueries(getAllCharacteristicMemberSupportReactions.getKey(projectId))
    clearResults()
  }

  const { showCalculateSnackbar } = useCalculateSnackbar()

  const stiffeningIntervalsQuery = useQuery({
    queryKey: getStiffeningIntervals.getKey(projectId),
    queryFn: ({ signal }) => getStiffeningIntervals.request(projectId, signal),
    onSuccess: setStiffeningIntervals,
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Aussteifenden Elemente'), {
        variant: 'error',
      }),
    refetchOnMount: true,
  })

  const elementLoadsQuery = useQuery({
    queryKey: getLoadsPerElement.getKey(projectId),
    queryFn: ({ signal }) => getLoadsPerElement.request(projectId, signal),
    onSuccess: setElementLoads,
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Lasten'), {
        variant: 'error',
      }),
    refetchOnMount: true,
  })

  useQuery({
    queryKey: getElementLoads.getKey(projectId),
    queryFn: () => getElementLoads.request(projectId),
    onSuccess: setManualLoadsPerElements,
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der definierten Lasten'), {
        variant: 'error',
      }),
    refetchOnMount: true,
  })

  const anchorInterventionsQuery = useQuery({
    queryKey: getAnchorInterventionTableData.getKey(projectId),
    queryFn: ({ signal }) => getAnchorInterventionTableData.request(projectId, signal),
    onSuccess: setAnchorInterventionTableData,
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Lastherkünfte'), {
        variant: 'error',
      }),
    refetchOnMount: true,
  })

  const anchorChecksQuery = useQuery({
    queryKey: getAnchorChecks.getKey(projectId),
    queryFn: ({ signal }) => getAnchorChecks.request(projectId, signal),
    onSuccess: setAnchorChecks,
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Lastherkünfte'), {
        variant: 'error',
      }),
    refetchOnMount: true,
  })

  const designPositionsQuery = useQuery({
    queryKey: getDesignPositionBundle.getKey(projectId),
    queryFn: ({ signal }) => getDesignPositionBundle.request(projectId, signal),
    onSuccess: (data: DesignPositionBundle | null) => {
      if (data) {
        setWallRipsPositionGrouping(data.position_grouping.wall_rips)
        setWallLintelsPositionGrouping(data.position_grouping.wall_lintels)
        setSlabBeamsPositionGrouping(data.position_grouping.slab_beams)
        setRoofSlabBeamsPositionGrouping(data.position_grouping.roof_slab_beams)
        setColumnsPositionGrouping(data.position_grouping.columns)
        setBeamsPositionGrouping(data.position_grouping.beams)
        setPurlinsPositionGrouping(data.position_grouping.purlins)
        setPositionLoads(data.loads_per_position)
      }
    },
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Design Positions'), {
        variant: 'error',
      }),
    enabled: !!elementLoadsQuery.data,
    refetchOnMount: true,
  })

  const memberCheckSettingsQuery = useQuery({
    queryKey: getMemberCheckSettings.getKey(projectId),
    queryFn: ({ signal }) => getMemberCheckSettings.request(projectId, signal),
    onSuccess: setMemberCheckSettings,
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Member Check Settings'), {
        variant: 'error',
      }),
    refetchOnMount: true,
    enabled: !!designPositionsQuery.data,
  })

  const designForcesQuery = useQuery({
    queryKey: getDesignForces.getKey(projectId),
    queryFn: ({ signal }) => getDesignForces.request(projectId, signal),
    onSuccess: setDesignForces,
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Design Forces'), {
        variant: 'error',
      }),
    refetchOnMount: true,
    enabled: !!memberCheckSettingsQuery.data,
  })

  useQuery({
    queryKey: getAllCharacteristicMemberSupportReactions.getKey(projectId),
    queryFn: async ({ signal }) => {
      return await getAllCharacteristicMemberSupportReactions.request(projectId, signal)
    },
    onSuccess: setCharacteristicMemberSupportReactions,
    onError: (error: AxiosError) =>
      enqueueSnackbar(
        buildErrorMessage(error, 'Fehler beim Laden der charakteristischen Auflagerkräfte'),
        { variant: 'error' },
      ),
    refetchOnMount: true,
    enabled: !!memberCheckSettingsQuery.data,
  })

  const elementCheckQuery = useQuery({
    queryKey: getElementChecksBundle.getKey(projectId),
    queryFn: ({ signal }) => getElementChecksBundle.request(projectId, signal),
    onSuccess: (data: ElementCheckBundle | null) => {
      if (data) {
        setStiffeningSegmentChecks(data.stiffening_element_checks)
        setStructuralChecks(data.structural_checks)
        setStandardRipChecks(data.standard_rip_checks)
      }
    },
    onError: (error: AxiosError) =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Element Checks'), {
        variant: 'error',
      }),
    enabled: !!memberCheckSettingsQuery.data,
    refetchOnMount: true,
  })

  const { mutate: compute, isLoading: isComputing } = useMutation(
    (projectId: string) => {
      clearResults()
      return computeResults.request(projectId)
    },
    {
      onSuccess: () => {
        triggerNotification({ message: 'Berechnung erfolgreich abgeschlossen ✅' })
        invalidateResults(projectId as string)

        queryClient.invalidateQueries(getResultsValid.getKey(projectId))
        queryClient.invalidateQueries(getHorizontalForcesPerSegment.getKey(projectId))
        queryClient.invalidateQueries(getModel.getKey(projectId))
        queryClient.invalidateQueries(getAnchorInterventionTableData.getKey(projectId))
        memberCheckSettings && setMemberCheckSettingsFromLastComputation(memberCheckSettings)
        setCrossSectionsFromLastComputation(elementCrossSectionAssignment)
      },
      onError: (error: AxiosError) => {
        triggerNotification({ message: 'Fehler bei der Berechnung ❌' })
        showCalculateSnackbar(buildErrorMessage(error, 'Fehler beim Starten der Berechung'))
      },
    },
  )

  const { mutate: computeAnchorChecks, isLoading: isComputingAnchorChecks } = useMutation(
    (projectId: string) => {
      return computeAnchorCheckResults.request(projectId)
    },
    {
      onSuccess: () => {
        // When finished computing, reset anchor checks store and refetch check results
        setAnchorChecks([])
        queryClient.invalidateQueries(getAnchorChecks.getKey(projectId))
        queryClient.invalidateQueries(getAnchorInterventionTableData.getKey(projectId))
        queryClient.invalidateQueries(getProjectConnectionPrioritization.getKey(projectId))
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Starten der Zugankerberechnung'), {
          variant: 'error',
          autoHideDuration: 20000,
        })
      },
    },
  )

  const isLoadingDesignChecks = elementCheckQuery.isFetching || memberCheckSettingsQuery.isFetching
  const isLoadingInitial = stiffeningIntervalsQuery.isFetching
  const initialLoadingSuccess = !!stiffeningIntervalsQuery.data
  const isLoadingElementLoads = elementLoadsQuery.isFetching
  const isLoadingDesignPositions = designPositionsQuery.isFetching
  const isLoadingDesignForces = designForcesQuery.isFetching
  const isLoadingAnchorChecksTable = anchorInterventionsQuery.isFetching
  const isLoadingAnchorChecksQuery = anchorChecksQuery.isFetching
  // const isLoadingCharacteristicSupportReactions = characteristicSupportReactionsQuery.isFetching

  const isLoadingResults =
    isLoadingDesignChecks ||
    isLoadingDesignPositions ||
    isComputing ||
    isLoadingDesignForces ||
    isLoadingElementLoads ||
    isLoadingAnchorChecksTable ||
    isLoadingAnchorChecksQuery

  const isLoadingResultsWithoutComputing =
    isLoadingDesignChecks ||
    isLoadingDesignPositions ||
    isLoadingDesignForces ||
    isLoadingElementLoads ||
    isLoadingAnchorChecksTable ||
    isLoadingAnchorChecksQuery

  return {
    compute,
    computeAnchorChecks,
    isComputing,
    isComputingAnchorChecks,
    isLoadingInitial,
    initialLoadingSuccess,
    isLoadingElementLoads,
    isLoadingDesignChecks,
    isLoadingDesignPositions,
    isLoadingDesignForces,
    isLoadingResults,
    isLoadingResultsWithoutComputing,
    abortLoadingResults,
  }
}

export default useResultsQueries
